import { createFocusManager } from "@react-aria/focus";
import type { ListState } from "@react-stately/list";
import { useCallback, type RefObject, useMemo, useState } from "react";
import type { DOMAttributes, FocusableElement } from "@react-types/shared";

import {
  useLayoutEffect,
  useResizeObserver,
  useValueEffect,
} from "@react-aria/utils";
import type { ToolbarButtonsProps } from "./types";

export interface ToolbarButtonsAria {
  toolbarButtonsProps: DOMAttributes;
  isMeasuring: boolean;
  visibleItems: number;
  /**
   * Index of the one item that is in the tab order (roving tabindex) —
   * `role="toolbar"` promises a single tab stop, so every other item is
   * excluded from it. Indexes address the rendered children: `0` to
   * `visibleItems - 1` are collection items and, when items have collapsed,
   * `visibleItems` is the overflow menu trigger.
   */
  rovingIndex: number;
}

export function useToolbarButtons<T>(
  props: ToolbarButtonsProps<T>,
  state: ListState<T>,
  ref: RefObject<FocusableElement>,
): ToolbarButtonsAria {
  const focusManager = createFocusManager(ref);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Buttons are direct children of the container, so their DOM position is
  // the index the roving tabindex is expressed in.
  const indexOfChild = useCallback(
    (element: Element | null) => {
      const children = ref.current?.children;

      if (!element || !children) return -1;

      return Array.prototype.indexOf.call(children, element);
    },
    [ref],
  );

  // Focus can also arrive by pointer or by tabbing in, not just by arrowing,
  // and the tab stop has to follow it in every case.
  const onFocus = (e: React.FocusEvent<FocusableElement>) => {
    const index = indexOfChild(e.target as Element);

    if (index >= 0) {
      setFocusedIndex(index);
    }
  };

  const isVertical = props.orientation === "vertical";
  // A toolbar navigates along its own axis; the orientation we declare with
  // aria-orientation has to be the one the keys honour.
  const nextItemKey = isVertical ? "ArrowDown" : "ArrowRight";
  const previousItemKey = isVertical ? "ArrowUp" : "ArrowLeft";

  const onKeyDown = (e: React.KeyboardEvent<FocusableElement>) => {
    if (!Boolean(e.currentTarget.contains(e.target as Node))) {
      return;
    }

    if (e.key !== nextItemKey && e.key !== previousItemKey) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // focusNext/focusPrevious walk focusable (not tabbable) children, so they
    // still reach the items the roving tabindex has excluded, and they skip
    // disabled ones because those are not focusable at all.
    const focused =
      e.key === nextItemKey
        ? focusManager.focusNext({ wrap: true })
        : focusManager.focusPrevious({ wrap: true });

    if (focused) {
      setFocusedIndex(indexOfChild(focused));
    }
  };

  const [{ isMeasuring, visibleItems }, setVisibleItems] = useValueEffect({
    visibleItems: state.collection.size,
    isMeasuring: false,
  });

  const visibleNodes = useMemo(
    () => [...state.collection].slice(0, visibleItems),
    [state.collection, visibleItems],
  );

  const canHoldTabStop = useCallback(
    (index: number) => {
      if (index < 0 || Boolean(props.isDisabled)) return false;

      // the overflow menu trigger renders right after the visible items
      if (index === visibleNodes.length) {
        return visibleItems < state.collection.size;
      }

      const item = visibleNodes[index] as
        | (typeof visibleNodes)[number]
        | undefined;

      if (item == null || Boolean(item.props.isSeparator)) return false;

      return !(
        state.disabledKeys.has(item.key) || Boolean(item.props.isDisabled)
      );
    },
    [
      props.isDisabled,
      state.collection.size,
      state.disabledKeys,
      visibleItems,
      visibleNodes,
    ],
  );

  const rovingIndex = useMemo(() => {
    if (focusedIndex !== null && canHoldTabStop(focusedIndex)) {
      return focusedIndex;
    }

    for (let index = 0; index <= visibleNodes.length; index++) {
      if (canHoldTabStop(index)) return index;
    }

    // nothing is reachable (an all-disabled toolbar); no button is tabbable
    // anyway, so the value only has to be stable.
    return 0;
  }, [canHoldTabStop, focusedIndex, visibleNodes.length]);

  const updateOverflow = useCallback(() => {
    // The collapse math is width-based; a vertical toolbar never collapses
    // items into the overflow menu.
    if (props.orientation === "vertical") {
      setVisibleItems(function* () {
        yield {
          visibleItems: state.collection.size,
          isMeasuring: false,
        };
      });

      return;
    }

    const computeVisibleItems = (visibleItems: number) => {
      if (ref.current) {
        const listItems = Array.from(ref.current.children) as HTMLLIElement[];
        const containerSize = ref.current.getBoundingClientRect().width;
        const isShowingMenu = visibleItems < state.collection.size;
        const lastItemIndex = isShowingMenu
          ? listItems.length - 2
          : listItems.length - 1;
        const minInlineSize = toNumber(
          window.getComputedStyle(listItems[lastItemIndex]).minInlineSize,
        );
        const gap = toNumber(window.getComputedStyle(ref.current).gap);
        let calculatedSize = 0;
        let newVisibleItems = 0;

        if (isShowingMenu) {
          const menuItem = listItems.pop();

          if (menuItem) {
            calculatedSize += menuItem.getBoundingClientRect().width + gap;
          }
        }

        for (const [i, item] of listItems.entries()) {
          const itemWidth = item.getBoundingClientRect().width;

          calculatedSize += itemWidth;

          if (i !== 0) {
            calculatedSize += gap;
          }

          if (calculatedSize <= containerSize) {
            newVisibleItems++;
          } else {
            const isSeparator = item.hasAttribute("data-separator");

            // check whether the truncated button will fit container
            if (
              !isSeparator &&
              calculatedSize - itemWidth + minInlineSize <= containerSize
            ) {
              newVisibleItems++;
            }

            break;
          }
        }

        return newVisibleItems;
      }

      return visibleItems;
    };

    setVisibleItems(function* () {
      // Update to show all items.
      yield {
        visibleItems: state.collection.size,
        isMeasuring: true,
      };

      const newVisibleItems = computeVisibleItems(state.collection.size);
      const isMeasuring =
        newVisibleItems < state.collection.size && newVisibleItems > 0;

      yield {
        visibleItems: newVisibleItems,
        isMeasuring,
      };

      // If the number of items is less than the number of children,
      // then update again to ensure that the menu fits.
      if (isMeasuring) {
        yield {
          visibleItems: computeVisibleItems(newVisibleItems),
          isMeasuring: false,
        };
      }
    });
  }, [ref, state.collection, setVisibleItems, props.orientation]);

  const parentRef = useMemo(
    () => ({
      get current() {
        return ref.current?.parentElement;
      },
    }),
    [ref],
  );

  useResizeObserver({
    ref: parentRef,
    onResize: updateOverflow,
  });
  useLayoutEffect(updateOverflow, [updateOverflow, state.collection]);

  return {
    toolbarButtonsProps: {
      role: "toolbar",
      "aria-label": props["aria-label"],
      // role="toolbar" is announced as horizontal unless told otherwise, so
      // the vertical layout has to declare its orientation.
      "aria-orientation": props.orientation ?? "horizontal",
      onFocus,
      onKeyDown,
    },
    isMeasuring,
    visibleItems,
    rovingIndex,
  };
}

function toNumber(value: string) {
  const parsed = parseInt(value, 10);

  return isNaN(parsed) ? 0 : parsed;
}
